import { useTranslation } from 'react-i18next';

/** Schema renderer; a future artifact can supply its own renderWidget adapter. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import WidgetGrid from './WidgetGrid';
import { answerWidgets } from './widgetData';
import QuotableText from './QuotableText';
import DashboardFilterField from './DashboardFilterField';
import { appliedFilters, inheritedFilterValue } from './dashboardFilters';
export { appliedFilters } from './dashboardFilters';

export function widgetFilters(specs, values) {
  const result = {};
  for (const spec of specs) {
    if (!(spec.id in values)) continue;
    for (const binding of spec.bindings) {
      result[binding.widget] = { ...result[binding.widget], [spec.id]: values[spec.id] };
    }
  }
  return result;
}

export function initialDates(dashboard) {
  const dates = {};
  for (const spec of dashboard.filters || []) {
    if (spec.kind !== 'date') continue;
    const ranges = spec.bindings.map((binding) => {
      const span = dashboard.widgets[binding.widget]?.query?.timeDimensions?.find((td) => td.dimension === binding.member)?.dateRange;
      return Array.isArray(span) && span.length === 2 && span.every((v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v))
        ? span.map((v) => v.slice(0, 10)) : null;
    });
    if (ranges[0] && ranges.every((r) => r && r[0] === ranges[0][0] && r[1] === ranges[0][1])) dates[spec.id] = ranges[0];
  }
  return dates;
}

export function inheritedFilters(source, dashboard, values, sourceState) {
  const before = sourceState?.dates || sourceState || initialDates(source || { filters: [] });
  const previous = source || (sourceState?.filters ? { filters: sourceState.filters } : undefined);
  const after = initialDates(dashboard);
  return Object.fromEntries((dashboard.filters || []).filter((spec) => {
    if (spec.kind !== 'date') return inheritedFilterValue(previous, spec, values[spec.id]) !== undefined;
    if (inheritedFilterValue(previous, spec, values[spec.id]) === undefined) return false;
    // An explicit period edit must take precedence over an earlier UI selection.
    return !before[spec.id] || !after[spec.id] || before[spec.id].join() === after[spec.id].join();
  }).map((spec) => [spec.id, inheritedFilterValue(previous, spec, values[spec.id])]));
}

export default React.memo(function DashboardView({ dashboard, messageId, renderWidget, filterValues, onFiltersChange, onReply }) {
  const { t: translate } = useTranslation();
  const defaults = useMemo(() => initialDates(dashboard), [dashboard]);
  const [draft, setDraft] = useState(() => ({ ...defaults, ...filterValues }));
  const [localValues, setLocalValues] = useState({});
  const values = filterValues ?? localValues;
  useEffect(() => {
    if (filterValues !== undefined) setDraft({ ...defaults, ...filterValues });
  }, [defaults, filterValues]);
  const setValues = (next) => { setLocalValues(next); onFiltersChange?.(next); };
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const draftError = useCallback((id, message) => setFieldErrors((current) => {
    if ((current[id] || '') === message) return current;
    return { ...current, [id]: message };
  }), []);
  const [expanded, setExpanded] = useState(false);
  const inlinePanel = useRef(null);
  const inlineHeight = useRef(0);
  useEffect(() => {
    if (expanded || typeof ResizeObserver !== 'function' || !inlinePanel.current) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry?.contentRect.height) inlineHeight.current = entry.contentRect.height;
    });
    observer.observe(inlinePanel.current);
    return () => observer.disconnect();
  }, [expanded]);
  const reply = useCallback((reference) => {
    setExpanded(false);
    onReply?.(reference);
  }, [onReply]);
  const replyAction = onReply ? reply : undefined;
  const specs = useMemo(() => dashboard.filters || [], [dashboard.filters]);
  const items = useMemo(() => answerWidgets({ widgets: dashboard.widgets }), [dashboard.widgets]);
  const filters = useMemo(() => widgetFilters(specs, values), [specs, values]);
  const apply = (e) => {
    e.preventDefault();
    const pendingError = specs.map((spec) => fieldErrors[spec.id]).find(Boolean);
    if (pendingError) { setError(pendingError); return; }
    const result = appliedFilters(specs, draft);
    setError(result.error || '');
    if (result.values) setValues(result.values);
  };
  const reset = () => { setDraft(defaults); setValues({}); setError(''); setFieldErrors({}); setResetKey((key) => key + 1); };
  const panel = (
    <>
      <QuotableText messageId={messageId} onReply={replyAction} source={{ kind: 'dashboard', field: 'title' }} label={translate('assistant.dashboardViewDashboard')}><Typography component="h2" variant="h6" fontWeight={800}>{dashboard.title}</Typography></QuotableText>
      {dashboard.description && <QuotableText messageId={messageId} onReply={replyAction} source={{ kind: 'dashboard', field: 'description' }} label={translate('assistant.dashboardViewDashboard')}><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{dashboard.description}</Typography></QuotableText>}
      {specs.length > 0 && (
        <Box component="form" onSubmit={apply} sx={{ mt: 2 }} aria-label={translate('assistant.dashboardViewDashboardFilters')}>
          <Box sx={{ display: 'flex', gap: 2, rowGap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {specs.map((spec) => (
              <Box key={spec.id} sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
                <DashboardFilterField key={`${spec.id}:${resetKey}`} spec={spec} value={draft[spec.id]} onChange={(v) => setDraft((d) => ({ ...d, [spec.id]: v }))} onDraftError={(message) => draftError(spec.id, message)} />
                {spec.bindings.length < items.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: 240 }}>{translate('assistant.dashboardFilterTargets', { titles: spec.bindings.map((b) => dashboard.widgets[b.widget]?.title).join(', ') })}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button size="small" type="submit" variant="contained">{translate('assistant.dashboardViewApply')}</Button>
            <Button size="small" onClick={reset}>{translate('assistant.dashboardViewReset')}</Button>
          </Box>
          {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
        </Box>
      )}
      {Object.keys(values).length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{translate('assistant.dashboardViewTheConclusionsInTheMessageReferTo')}</Typography>
      )}
      <WidgetGrid messageId={messageId} items={items} filters={filters} renderWidget={renderWidget} onReply={replyAction} />
      {dashboard.notes?.length > 0 && (
        <Box component="ul" sx={{ mb: 0, pl: 2, color: 'text.secondary' }}>
          {dashboard.notes.map((note, i) => <Box component="li" key={i}><QuotableText messageId={messageId} onReply={replyAction} source={{ kind: 'dashboard', field: 'note', note_index: i }} label={translate('assistant.dashboardViewDashboard')}><Typography component="span" variant="body2">{note}</Typography></QuotableText></Box>)}
        </Box>
      )}
    </>
  );
  return (
    <Paper component="section" variant="outlined" aria-label={translate('assistant.dashboardViewDashboard2', { value1: dashboard.title })} sx={{ p: 2, mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={translate('assistant.dashboardViewOpenFullScreen')}><IconButton size="small" aria-label={translate('assistant.dashboardViewOpenDashboardFullScreen')} onClick={() => setExpanded(true)}><OpenInFullIcon fontSize="small" /></IconButton></Tooltip>
      </Box>
      <Box ref={inlinePanel} sx={{ minHeight: expanded ? inlineHeight.current : undefined }}>{!expanded && panel}</Box>
      <Dialog fullScreen open={expanded} onClose={() => setExpanded(false)} aria-label={dashboard.title}>
        <Box data-dashboard-scroll="true" sx={{ p: { xs: 2, md: 4 }, flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}><IconButton aria-label={translate('assistant.dashboardViewCloseDashboard')} onClick={() => setExpanded(false)}><CloseIcon /></IconButton></Box>
          {expanded && panel}
        </Box>
      </Dialog>
    </Paper>
  );
});
